import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import { CommonModule, NgIf, AsyncPipe } from '@angular/common';
import { HighlightModule } from 'ngx-highlightjs';
import { Observable, Subject } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient, HttpClientModule } from '@angular/common/http';

declare const _z: any;

interface ModalTemplate {
  templateName: string;
  overlayFadeInString: number;
  overlayFadeOutString: number;
  overlayColor: string;
  overlayOpacity: number;
  modalColor: string;
  modalOpacity: number;
  modalHeight: number;
  modalWidth: number;
  innerHTML: string;
}

@Component({
  selector: 'modal-builder',
  templateUrl: './modal-builder.component.html',
  styleUrls: ['./modal-builder.component.scss'],
  standalone: true,
  imports: [NgIf, HighlightModule, AsyncPipe, CommonModule, FormsModule, HttpClientModule],
})
export class ModalBuilderComponent implements OnInit, OnDestroy {
  user: User;
  singleUser$: Observable<User>;
  unsubscribe$: Subject<void> = new Subject();

  baseCSS: any = null;

  constructor(
    public authService: AuthService,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  vm: any = {
    name: '',
    triggerTypes: ['Entry', 'Exit'],
    triggerType: 'Entry',
    overlayClose: false,
    designTemplates: [] as ModalTemplate[],
    selectedTemplate: null as ModalTemplate | null,
  };

  ngOnInit(): void {
    this.loadBaseCSS();
    this.loadDrafts();

    this.vm.designTemplates = [
      {
        templateName: 'Demo1',
        overlayFadeInString: 400,
        overlayFadeOutString: 400,
        overlayColor: '#000000',
        overlayOpacity: 80,
        modalColor: '#9fffff',
        modalOpacity: 100,
        modalHeight: 30,
        modalWidth: 20,
        innerHTML: '<h1 style="color:#fff;text-align:center">Demo 1!</h1>',
      },
      {
        templateName: 'Demo2',
        overlayFadeInString: 400,
        overlayFadeOutString: 400,
        overlayColor: '#000000',
        overlayOpacity: 80,
        modalColor: '#ffffff',
        modalOpacity: 90,
        modalHeight: 60,
        modalWidth: 30,
        innerHTML: '<h1 style="color:#000;text-align:center">Demo 2!</h1>',
      },
    ];

    this.vm.selectedTemplate = this.vm.designTemplates[0];
  }

  /** 🔹 Load base modal CSS (JSON) */
  private loadBaseCSS(): void {
    this.http.get('assets/js/z.json').subscribe({
      next: (data) => (this.baseCSS = data),
      error: (err) => console.error('Failed to load z.json:', err),
    });
  }

  /** 🔹 Load saved drafts */
  private loadDrafts(): void {
    try {
      const saved = localStorage.getItem('modals');
      if (saved) {
        const drafts = JSON.parse(saved);
        if (drafts.length > 0) {
          const last = drafts[drafts.length - 1];
          this.vm.name = last.name || '';
          this.vm.triggerType = last.triggerType || 'Entry';
          this.vm.overlayClose = last.overlayClose || false;
          this.vm.selectedTemplate = last.selectedTemplate || this.vm.designTemplates[0];
        }
      }
    } catch {
      console.warn('No valid drafts in localStorage.');
    }
  }

  /** 🔹 Save modal draft */
  private saveDraft(modalPayload: any): void {
    try {
      const existing = JSON.parse(localStorage.getItem('modals') || '[]');
      existing.push(modalPayload);
      localStorage.setItem('modals', JSON.stringify(existing));
    } catch (err) {
      console.error('Failed to save modal draft:', err);
    }
  }

  /** 🔹 Safe render for preview (Angular sandbox) */
  safeHTML(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  bindEditor(): void {}

  /** 🔹 Build CSS for live preview & modal payload */
  private buildTemplateCSS(t: ModalTemplate): string {
    if (!this.baseCSS) return '';

    const toHex = (val: string) => (val.startsWith('#') ? val : `#${val}`);
    const merged = JSON.parse(JSON.stringify(this.baseCSS));

    const apply = (sel: string, prop: string, val: any) => {
      if (!merged[sel]) merged[sel] = {};
      merged[sel][prop] = val;
    };

    apply('#md .o', 'background-color', toHex(t.overlayColor));
    apply('#md .o', 'opacity', t.overlayOpacity / 100);

    apply('#md .m', 'background-color', toHex(t.modalColor));
    apply('#md .m', 'opacity', t.modalOpacity / 100);
    apply('#md .m', 'width', `${t.modalWidth}%`);
    apply('#md .m', 'height', `${t.modalHeight}%`);

    apply('#md .c', 'width', `${t.modalWidth}%`);
    apply('#md .c', 'height', `${t.modalHeight}%`);

    return this.jsonToCSS(merged);
  }

  /** 🔹 Convert JSON → CSS */
  private jsonToCSS(json: any): string {
    let css = '';
    for (const selector in json) {
      if (json.hasOwnProperty(selector)) {
        css += `${selector}{`;
        for (const prop in json[selector]) {
          if (json[selector].hasOwnProperty(prop)) {
            css += `${prop}:${json[selector][prop]};`;
          }
        }
        css += '}';
      }
    }
    return css;
  }

  /** 🔹 TEST MODAL BUTTON — only time _z.js is called */
  testModal(save = true): void {
    if (!this.vm.selectedTemplate) return;

    const t = this.vm.selectedTemplate;
    const modalPayload = {
      name: this.vm.name || t.templateName,
      triggerType: this.vm.triggerType,
      overlayClose: this.vm.overlayClose,
      selectedTemplate: {
        overlayFadeIn: 1000 - Number(t.overlayFadeInString || 400),
        overlayFadeOut: 1000 - Number(t.overlayFadeOutString || 400),
        overlayOpacity: t.overlayOpacity,
        overlayColor: t.overlayColor.replace('#', ''),
        modalOpacity: t.modalOpacity,
        modalColor: t.modalColor.replace('#', ''),
        modalHeight: t.modalHeight,
        modalWidth: t.modalWidth,
        innerHTML: t.innerHTML,
      },
      minifiedCSS: this.buildTemplateCSS(t),
    };

    if (save) this.saveDraft(modalPayload);

    // ✅ Only run z.js modal here
    try {
      if (typeof _z !== 'undefined' && _z.md?.Test) {
        console.log('🚀 Launching modal via _z.md.Test()');
        _z.md.Test(modalPayload);
      } else {
        console.warn('⚠️ _z.md not found — ensure z.js is loaded.');
      }
    } catch (err) {
      console.error('Modal launch failed:', err);
    }
  }

  logout() {
    this.authService.logout();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }
}
